import React, { useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";

const PORTRAIT =
  "https://customer-assets-v7afamib.emergentagent.net/job_design-motion-48/artifacts/cwehb40c_YSF08394-1-BW.webp";

const LOGO_V = "v=8";
// `route` points each brand at its showcase page (or /work/private for
// clients whose work is under NDA — a single friendly screen shows a
// contact line instead of the private videos).
const BRANDS = [
  { slug: "dominos",       client: "Domino's",         logo: `/logos/dominos.png?${LOGO_V}`,             route: "/work/private" },
  { slug: "fci",           client: "FCI",              logo: `/logos/fci.png?${LOGO_V}`,                 route: "/work/fci" },
  { slug: "saksikampus",   client: "Saksı Kampüs",     logo: `/logos/saksikampus-color.png?${LOGO_V}`,   logoHover: `/logos/saksikampus-dark-hover.png?${LOGO_V}`, logoLight: `/logos/saksikampus-light.png?${LOGO_V}`, route: "/work/saksi-kampus", preserveColors: true },
  { slug: "hdi",           client: "HDİ Sigorta",      logo: `/logos/hdi.png?${LOGO_V}`,                 route: "/work/hdi-sigorta" },
  { slug: "ome",           client: "Ome Real Estate",  logo: `/logos/ome.png?${LOGO_V}`,                 route: "/work/private" },
  { slug: "isteyapayzeka", client: "İş'te Yapay Zeka", logo: `/logos/isteyapayzeka.png?${LOGO_V}`,       route: "/work/iste-yapay-zeka" },
  { slug: "patika",        client: "Patika Akademi",   logo: `/logos/patika.png?${LOGO_V}`,              route: "/work/patika-akademi" },
  { slug: "fiyuu",         client: "Fiyuu",            logo: `/logos/fiyuu.png?${LOGO_V}`,               route: "/work/fiyuu" },
  { slug: "akkok",         client: "Akkök Holding",    logo: `/logos/akkok.png?${LOGO_V}`,               route: "/work/private" },
];

const HIGHLIGHTS = [
  "After Effects", "Premiere Pro",
  "motion design",
  "video editing",
  "brand storytelling",
  "AI-supported tools",
];
const HL_RE = new RegExp(`(${HIGHLIGHTS.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`, "gi");
const highlight = (text) =>
  text.split(HL_RE).map((part, i) =>
    HIGHLIGHTS.some((p) => p.toLowerCase() === part.toLowerCase()) ? (
      <span key={i} className="text-navy-ink font-normal">{part}</span>
    ) : (
      part
    )
  );

export const About = React.forwardRef(({ onFilterByClient }, ref) => {
  const { t } = useLang();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const firstPara = t.about.longBio[0];

  const handleBrandClick = (e, brand) => {
    e.preventDefault();
    navigate(brand.route);
  };

  return (
    <section
      ref={ref}
      id="about"
      data-testid="about-section"
      className="relative border-t border-border bg-void"
    >
      <div ref={sectionRef} className="mx-auto max-w-[1600px] px-6 md:px-12 py-28 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: heading + portrait (heading above) */}
          <div className="lg:col-span-5">
            <h2 className="font-display text-6xl md:text-8xl font-bold uppercase tighter leading-[0.85] text-bone mb-8 md:mb-10">
              {t.about.heading}
            </h2>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-xs overflow-hidden"
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img
                  src={PORTRAIT}
                  alt="Polat Pala"
                  className="h-full w-full object-cover contrast-110"
                  style={{ objectPosition: "center 25%" }}
                  data-testid="about-portrait"
                />
              </div>
            </motion.div>
          </div>

          {/* Right: first paragraph + MORE button */}
          <div className="lg:col-span-7 lg:pt-6">
            <div className="relative max-w-2xl min-h-[300px] md:min-h-[360px] flex flex-col">
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="font-body font-light text-lg md:text-xl text-bone/75 leading-relaxed"
              >
                {highlight(firstPara)}
              </motion.p>

              {/* MORE button — bottom right */}
              <div className="mt-10 md:mt-auto flex justify-end">
                <motion.button
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.25, duration: 0.6 }}
                  onClick={() => navigate("/about")}
                  data-testid="about-more-btn"
                  className="group inline-flex items-center gap-4 font-display text-3xl md:text-5xl font-bold uppercase tighter leading-[0.9] text-bone hover:text-navy-ink transition-colors duration-300"
                >
                  <span>More</span>
                  <span className="inline-block translate-x-0 group-hover:translate-x-3 transition-transform duration-500">→</span>
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted by brands (stays on home) */}
        <div className="mt-24 md:mt-32 border-t border-border pt-12">
          <h3
            data-testid="about-brands-title"
            className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tighter leading-[0.9] text-bone whitespace-normal 2xl:whitespace-nowrap break-words"
          >
            {t.about.brandsTitle}
          </h3>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-6 sm:gap-x-10 gap-y-12 items-center">
            {BRANDS.map((b, i) => (
              <motion.a
                key={b.slug}
                href={b.route}
                onClick={(e) => handleBrandClick(e, b)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                aria-label={`View ${b.client} projects`}
                data-testid={`brand-${b.slug}`}
                className="brand-link group flex h-14 w-full items-center justify-center"
              >
                {b.preserveColors ? (
                  <span className="relative inline-flex h-full w-full max-w-[160px] items-center justify-center">
                    <img
                      src={b.logo}
                      alt={b.client}
                      className="brand-preserve-base max-h-14 max-w-full object-contain transition-opacity duration-400 ease-out"
                    />
                    {b.logoHover && (
                      <img
                        src={b.logoHover}
                        alt=""
                        aria-hidden
                        className="brand-preserve-hover absolute inset-0 m-auto max-h-14 max-w-full object-contain opacity-0 transition-opacity duration-400 ease-out"
                      />
                    )}
                    {b.logoLight && (
                      <img
                        src={b.logoLight}
                        alt=""
                        aria-hidden
                        className="brand-preserve-lightmode absolute inset-0 m-auto max-h-14 max-w-full object-contain opacity-0 transition-opacity duration-400 ease-out"
                      />
                    )}
                  </span>
                ) : (
                  <span
                    role="img"
                    aria-hidden
                    className="brand-logo block h-full w-full max-w-[160px]"
                    style={{
                      WebkitMaskImage: `url(${b.logo})`,
                      maskImage: `url(${b.logo})`,
                    }}
                  />
                )}
              </motion.a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

About.displayName = "About";
